import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import OrnateDivider from '../components/OrnateDivider';
import WhatsAppCTA from '../components/WhatsAppCTA';
import EnrollBlock from '../components/EnrollBlock';
import EditableText from '../components/EditableText';

/* ---------- Assets ---------- */
const HERO_IMG =
  'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/ico6mczy_New%20Hero%20Image.png';
const SIDE_IMG_1 =
  'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/8awl2gn9_Side%20Image1.png';
const SIDE_IMG_2 =
  'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/k7vzkegw_Side%20image%202.png';

const GALLERY = [
  {
    src: 'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/j7zgoout_Gallery%201.png',
    alt: 'Asana — side angle pose by lamplight',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/bs2w7uwr_Gallery%202.png',
    alt: 'Arm-balance silhouette at the window',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/8jbay0xx_Gallery%203.png',
    alt: 'Crow pose silhouette by the window',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/ejb3x0yk_Gallery%204.png',
    alt: 'Dancer pose at sunset over the sea',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/7uzi5sy0_Gallery%205.png',
    alt: 'Ritual ceremony with bell and incense',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/ast43bsz_Gallery%206.png',
    alt: 'Standing split silhouette by lamplight',
  },
];

/* ---------- Biography copy ---------- */
/* ---------- Biography copy (default — admin can override via EditableText) ---------- */
const SANJAY_BIO_DEFAULT = [
  'Only when true silence guides worldly life can one fully embody Mother Shakti’s divine expression — tasting the Nava Rasas and experiencing true divine love.',
  'With over 12 years of sincere sadhana in Yoga and Tantra, Sanjay Ji walks this path with deep humility and reverence.',
  'He is an Acharya in Siddha Ashtanga Yog (Vrata Yog) in the lineage of Maharishi Shring, Kundalini Tantra of Kubjika Devi, and Vijnana Bhairava Tantra. He has received formal Deeksha initiation into the ancient Kulluta Siddha Tantra and Yog tradition from Kaulantak Nath, His Holiness Mahasiddha Ishaputra Ji.',
  'His practice is deeply rooted in the living Kulluta Siddha Tradition of the Himalayas, supported by the grace of his Gurus and the blessings of the Divine Mother. He is also grateful to Maharaj Swami Ji of Gangotri (Gangnani Wale) for the wisdom of Advaita Vedanta.',
  'In this tradition, true growth arises through consistent practice and inner silence. Personal realizations are kept sacred and shared only with one’s Guru or guide.',
  'All that is offered here flows from practice, Guru’s grace, and the living lineage. There is no claim of mastery — only a humble dedication to support genuine seekers.',
  'His deepest sankalpa lies in Siddha Dharma — a path beyond religions, dedicated to the awakening and enlightenment of the masses for world peace and harmony.',
  'If a sincere calling arises, he is here to support the journey with humility and care.',
].join('\n\n');

/* ---------- Reusable: image card ---------- */
const ImageCard = ({ src, alt, testId, ratio = 'aspect-square' }) => (
  <figure
    className="rounded-xl overflow-hidden border border-primary/20 bg-black/30"
    data-testid={testId}
  >
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`w-full h-full block object-cover ${ratio}`}
      style={{ filter: 'saturate(0.95) contrast(1.02)' }}
    />
  </figure>
);

/* ---------- Gallery (horizontal scroller) ---------- */
const Gallery = () => {
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('[data-gallery-card]');
    const step = card ? card.getBoundingClientRect().width + 24 : 320;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section className="mt-16" data-testid="guides-gallery-section">
      <div className="text-center mb-8">
        <OrnateDivider variant="diamond" className="mb-5 max-w-md mx-auto" />
        <h2 className="text-3xl sm:text-4xl font-heading font-bold golden-text text-glow tracking-wide">
          <EditableText
            id="guides.gallery.title"
            defaultValue="Moments from the Path"
          />
        </h2>
      </div>

      <div className="relative">
        <button
          type="button"
          aria-label="Scroll gallery left"
          onClick={() => scrollBy(-1)}
          className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 border border-primary/30 text-primary hover:bg-primary/15 transition items-center justify-center backdrop-blur"
          data-testid="gallery-prev"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          aria-label="Scroll gallery right"
          onClick={() => scrollBy(1)}
          className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 border border-primary/30 text-primary hover:bg-primary/15 transition items-center justify-center backdrop-blur"
          data-testid="gallery-next"
        >
          <ChevronRight size={20} />
        </button>

        <div
          ref={scrollerRef}
          className="flex gap-5 sm:gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 -mx-4 sm:-mx-0 px-4 sm:px-0 scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-testid="gallery-scroller"
        >
          {GALLERY.map((img, idx) => (
            <div
              key={`gallery-${idx}`}
              data-gallery-card
              className="snap-start shrink-0 w-[78vw] sm:w-[320px] md:w-[340px] lg:w-[360px]"
              data-testid={`gallery-card-${idx + 1}`}
            >
              <ImageCard src={img.src} alt={img.alt} testId={`gallery-img-${idx + 1}`} />
            </div>
          ))}
        </div>
      </div>

      <p
        className="mt-3 text-right text-[10px] sm:text-[11px] text-primary/40 italic pr-1"
        data-testid="gallery-edited-note"
      >
        <EditableText
          id="guides.gallery.caption"
          defaultValue="Images taken and auto AI edited to suit the website’s theme"
        />
      </p>
    </section>
  );
};

/* ---------- Guide Card (shared between Sanjay & future guides) ---------- */
const GuideCard = ({
  name,
  role,
  heroSrc,
  sideSrcs = [],
  children,
  testId,
}) => {
  const hasImages = Boolean(heroSrc) || sideSrcs.length > 0;

  return (
    <section
      className="card-mystical rounded-2xl p-6 sm:p-10"
      data-testid={testId}
    >
      {/* Header — left aligned */}
      <header className="mb-7">
        <h2
          className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold golden-text text-glow tracking-wide mb-2"
          data-testid={`${testId}-name`}
        >
          {name}
        </h2>
        <p
          className="text-[11px] sm:text-xs font-heading tracking-[0.35em] text-primary/65 uppercase"
          data-testid={`${testId}-role`}
        >
          {role}
        </p>
      </header>

      {/* Body grid: Hero (left) · Biography (middle) · Side images (right) */}
      {hasImages ? (
        <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)_220px] gap-6 lg:gap-8 items-start">
          {/* Left: Hero portrait */}
          {heroSrc && (
            <div className="lg:sticky lg:top-28">
              <ImageCard
                src={heroSrc}
                alt={`${name} — portrait`}
                testId={`${testId}-hero`}
              />
            </div>
          )}

          {/* Middle: biography text — left aligned */}
          <div
            className="space-y-4 text-primary/85 leading-relaxed text-[15px] sm:text-base text-left"
            data-testid={`${testId}-body`}
          >
            {children}
          </div>

          {/* Right: Side images stacked */}
          {sideSrcs.length > 0 && (
            <aside
              className="lg:sticky lg:top-28 grid grid-cols-2 lg:grid-cols-1 gap-4 lg:gap-5"
              data-testid={`${testId}-side-images`}
            >
              {sideSrcs.map((s, i) => (
                <ImageCard
                  key={`side-${i}`}
                  src={s.src}
                  alt={s.alt || `${name} — sadhana ${i + 1}`}
                  testId={`${testId}-side-${i + 1}`}
                />
              ))}
            </aside>
          )}
        </div>
      ) : (
        // No images — text only (used by section 2 for now)
        <div
          className="space-y-4 text-primary/85 leading-relaxed text-[15px] sm:text-base text-left"
          data-testid={`${testId}-body`}
        >
          {children}
        </div>
      )}
    </section>
  );
};

/* ---------- Page ---------- */
const Guides = () => {
  return (
    <div className="min-h-screen mystical-bg pt-28 pb-20 relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          {/* ===== Page header (centred eyebrow) ===== */}
          <div className="text-center mb-10">
            <OrnateDivider label="Guides" variant="diamond" className="mb-6 max-w-xl mx-auto" />
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold golden-text text-glow tracking-wider"
              data-testid="guides-page-title"
            >
              <EditableText
                id="guides.page.title"
                defaultValue="The Circle of Guides"
              />
            </h1>
            <p className="text-sm text-primary/65 mt-2 italic max-w-xl mx-auto">
              <EditableText
                id="guides.page.subtitle"
                defaultValue="The lineage and the senior practitioners who hold this path together."
              />
            </p>
          </div>

          {/* ===== Section 1: Sanjay Ji ===== */}
          <GuideCard
            name={<EditableText id="guides.sanjay.name" defaultValue="Sanjay Ji" />}
            role={<EditableText id="guides.sanjay.role" defaultValue="Founder · Lineage Holder" />}
            heroSrc={HERO_IMG}
            sideSrcs={[
              { src: SIDE_IMG_1, alt: 'Sadhana — meditation posture' },
              { src: SIDE_IMG_2, alt: 'Sadhana — surya namaskar at sunset' },
            ]}
            testId="guide-card-sanjay"
          >
            <EditableText
              id="guides.sanjay.bio"
              defaultValue={SANJAY_BIO_DEFAULT}
              as="div"
              multiline
              paragraphs
            />
          </GuideCard>

          {/* ===== Gallery ===== */}
          <Gallery />
        </motion.div>

        {/* ===== Two-option enroll block ===== */}
        <div className="mt-16">
          <EnrollBlock />
        </div>

        {/* ===== WhatsApp community CTA (unchanged) ===== */}
        <div className="mt-12">
          <OrnateDivider variant="diamond" className="mb-4 max-w-xl mx-auto" />
          <WhatsAppCTA
            title="Join the Siddha Samaj Community"
            subtitle="A space for serious practitioners. Ask, practice, and stay aligned."
            subtitleItalic
          />
        </div>
      </div>
    </div>
  );
};

export default Guides;
